import React, { useState } from 'react';
import { useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi';
import { parseEther } from 'viem';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { 
    Scale, 
    Clock, 
    AlertTriangle,
    ExternalLink,
    Loader2,
    Shield,
    Gavel,
    Users,
    Timer,
    CheckCircle,
    XCircle
} from 'lucide-react';
import { getExplorerUrl, WETH_ADDRESSES } from '../lib/contract';

// UMA OptimisticOracleV3 ABI for dispute
const UMA_ORACLE_ABI = [
    {
        "type": "function",
        "name": "disputeAssertion",
        "inputs": [
            { "name": "assertionId", "type": "bytes32" },
            { "name": "disputer", "type": "address" }
        ],
        "outputs": [],
        "stateMutability": "nonpayable"
    },
    {
        "type": "function",
        "name": "getAssertion",
        "inputs": [{ "name": "assertionId", "type": "bytes32" }],
        "outputs": [{
            "name": "",
            "type": "tuple",
            "components": [
                { "name": "escalationManagerSettings", "type": "tuple", "components": [] },
                { "name": "asserter", "type": "address" },
                { "name": "assertionTime", "type": "uint64" },
                { "name": "settled", "type": "bool" },
                { "name": "currency", "type": "address" },
                { "name": "expirationTime", "type": "uint64" },
                { "name": "settlementResolution", "type": "bool" },
                { "name": "domainId", "type": "bytes32" },
                { "name": "identifier", "type": "bytes32" },
                { "name": "bond", "type": "uint256" },
                { "name": "callbackRecipient", "type": "address" },
                { "name": "disputer", "type": "address" }
            ]
        }],
        "stateMutability": "view"
    },
    {
        "type": "function",
        "name": "getMinimumBond",
        "inputs": [{ "name": "currency", "type": "address" }],
        "outputs": [{ "name": "", "type": "uint256" }],
        "stateMutability": "view"
    }
];

// WETH ABI
const WETH_ABI = [
    {
        "type": "function",
        "name": "approve",
        "inputs": [
            { "name": "spender", "type": "address" },
            { "name": "amount", "type": "uint256" }
        ],
        "outputs": [{ "name": "", "type": "bool" }],
        "stateMutability": "nonpayable"
    }
];

export function OracleDisputePanel({ 
    bet, 
    chainId, 
    address,
    oracleAddress 
}) {
    const [step, setStep] = useState('info'); // info, approve, dispute
    const [timeRemaining, setTimeRemaining] = useState(null);
    
    const assertionId = bet?.assertion_id;
    const creatorWinsAsserted = bet?.creator_wins_asserted;
    const assertionTime = bet?.assertion_requested_at;
    
    // Calculate time remaining for challenge
    React.useEffect(() => {
        if (!assertionTime) return;
        
        const updateTime = () => {
            const assertedAt = new Date(assertionTime);
            const expiresAt = new Date(assertedAt.getTime() + 2 * 60 * 60 * 1000); // 2 hours
            const now = new Date();
            const remaining = expiresAt - now;
            
            if (remaining <= 0) {
                setTimeRemaining(null);
            } else {
                const hours = Math.floor(remaining / 3600000);
                const minutes = Math.floor((remaining % 3600000) / 60000);
                const seconds = Math.floor((remaining % 60000) / 1000);
                setTimeRemaining({ hours, minutes, seconds, total: remaining, progress: (remaining / (2 * 60 * 60 * 1000)) * 100 });
            }
        };
        
        updateTime();
        const interval = setInterval(updateTime, 1000);
        return () => clearInterval(interval);
    }, [assertionTime]);
    
    // WETH approval
    const { 
        writeContract: approveWeth, 
        data: approveHash,
        isPending: isApprovePending
    } = useWriteContract();
    
    const { isLoading: isApproveConfirming, isSuccess: isApproveSuccess } = 
        useWaitForTransactionReceipt({ hash: approveHash });
    
    // Dispute contract call
    const { 
        writeContract: disputeAssertion, 
        data: disputeHash,
        isPending: isDisputePending,
        error: disputeError
    } = useWriteContract();
    
    const { isLoading: isDisputeConfirming, isSuccess: isDisputeSuccess } = 
        useWaitForTransactionReceipt({ hash: disputeHash });
    
    const handleApprove = async () => {
        const wethAddress = WETH_ADDRESSES[chainId];
        if (!wethAddress || !oracleAddress) {
            toast.error('Configuration error');
            return;
        }
        
        try {
            await approveWeth({
                address: wethAddress,
                abi: WETH_ABI,
                functionName: 'approve',
                args: [oracleAddress, parseEther('0.1')]
            });
        } catch (error) {
            console.error('Approval error:', error);
            toast.error('Failed to approve WETH');
        }
    };
    
    const handleDispute = async () => {
        if (!assertionId || !oracleAddress || !address) return;
        
        try {
            await disputeAssertion({
                address: oracleAddress,
                abi: UMA_ORACLE_ABI,
                functionName: 'disputeAssertion',
                args: [`0x${assertionId}`, address]
            });
        } catch (error) {
            console.error('Dispute error:', error);
            toast.error('Failed to dispute assertion');
        }
    };
    
    // Effect for approval success
    React.useEffect(() => {
        if (isApproveSuccess) {
            toast.success('WETH approved for dispute bond!');
            setStep('dispute');
        }
    }, [isApproveSuccess]);
    
    // Effect for dispute success
    React.useEffect(() => {
        if (isDisputeSuccess && disputeHash) {
            toast.success('Assertion disputed! UMA voters will decide.', {
                description: (
                    <a 
                        href={getExplorerUrl(chainId, 'tx', disputeHash)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00FF66] hover:underline flex items-center gap-1"
                    >
                        View transaction <ExternalLink className="w-3 h-3" />
                    </a>
                ),
            });
        }
    }, [isDisputeSuccess, disputeHash, chainId]);
    
    // Only show if bet is in assertion and time remaining
    if (bet?.status !== 'in_assertion' || !assertionId) {
        return null;
    }
    
    const isExpired = !timeRemaining;
    const isUserAsserter = bet?.metadata?.asserter?.toLowerCase() === address?.toLowerCase();
    
    return (
        <Card className="bg-[#FFD700]/5 border-[#FFD700]/20">
            <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Gavel className="w-5 h-5 text-[#FFD700]" />
                    Challenge Assertion
                </CardTitle>
                <CardDescription className="text-white/50">
                    Dispute the current assertion if you believe it's wrong
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Current Assertion Status */}
                <div className="p-4 rounded-lg bg-white/[0.03] border border-white/5">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-white/60">Current Assertion</span>
                        <Badge className={creatorWinsAsserted ? 'bg-[#00FF66]/20 text-[#00FF66]' : 'bg-[#6F3FCA]/20 text-[#9B6FE8]'}>
                            {creatorWinsAsserted ? 'Creator Wins' : 'Taker Wins'}
                        </Badge>
                    </div>
                    
                    {/* Time Remaining */}
                    {timeRemaining ? (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 text-[#FFD700]">
                                    <Timer className="w-4 h-4" />
                                    <span>Challenge Window</span>
                                </div>
                                <span className="font-mono text-white">
                                    {String(timeRemaining.hours).padStart(2, '0')}:
                                    {String(timeRemaining.minutes).padStart(2, '0')}:
                                    {String(timeRemaining.seconds).padStart(2, '0')}
                                </span>
                            </div>
                            <Progress value={timeRemaining.progress} className="h-2 bg-white/10" />
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-[#00FF66]">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-sm">Challenge window expired - awaiting settlement</span>
                        </div>
                    )}
                </div>
                
                {/* Assertion ID */}
                <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                    <div className="text-xs text-white/40 mb-1">Assertion ID</div>
                    <div className="font-mono text-xs text-white/70 break-all">
                        0x{assertionId}
                    </div>
                </div>
                
                {isExpired ? (
                    <Alert className="border-[#00FF66]/20 bg-[#00FF66]/5">
                        <CheckCircle className="w-4 h-4 text-[#00FF66]" />
                        <AlertDescription className="text-white/60 text-sm">
                            The challenge period has ended. The assertion will be finalized automatically.
                        </AlertDescription>
                    </Alert>
                ) : isUserAsserter ? (
                    <Alert className="border-white/10 bg-white/5">
                        <Shield className="w-4 h-4 text-white/60" />
                        <AlertDescription className="text-white/60 text-sm">
                            You made this assertion. You cannot dispute your own assertion.
                        </AlertDescription>
                    </Alert>
                ) : (
                    <>
                        {/* Info Step */}
                        {step === 'info' && (
                            <div className="space-y-4">
                                <Alert className="border-orange-400/20 bg-orange-400/5">
                                    <AlertTriangle className="w-4 h-4 text-orange-400" />
                                    <AlertDescription className="text-white/60 text-sm">
                                        <strong className="text-orange-400">Warning:</strong> Disputing requires posting a WETH bond. 
                                        If the UMA voters determine you were wrong, you lose your bond.
                                    </AlertDescription>
                                </Alert>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                                        <Users className="w-4 h-4 text-[#9B6FE8] mb-1" />
                                        <div className="text-white/40">Resolution</div>
                                        <div className="text-white">UMA Token Holders Vote</div>
                                    </div>
                                    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                                        <Clock className="w-4 h-4 text-[#FFD700] mb-1" />
                                        <div className="text-white/40">Duration</div>
                                        <div className="text-white">~48-72 hours</div>
                                    </div>
                                </div>
                                
                                <Button
                                    className="w-full bg-orange-500 text-white hover:bg-orange-600"
                                    onClick={() => setStep('approve')}
                                >
                                    <Gavel className="w-4 h-4 mr-2" />
                                    I want to dispute this assertion
                                </Button>
                            </div>
                        )}
                        
                        {/* Approve Step */}
                        {step === 'approve' && (
                            <div className="space-y-4">
                                <div className="text-sm text-white/70">
                                    Step 1: Approve WETH for dispute bond
                                </div>
                                
                                {approveHash && (
                                    <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                                        <div className="text-xs text-[#00FF66] mb-1">Approval Transaction</div>
                                        <a 
                                            href={getExplorerUrl(chainId, 'tx', approveHash)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-xs text-white hover:text-[#00FF66] flex items-center gap-1"
                                        >
                                            {approveHash.slice(0, 10)}...{approveHash.slice(-8)}
                                            <ExternalLink className="w-3 h-3" />
                                        </a>
                                    </div>
                                )}
                                
                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        className="flex-1 border-white/10"
                                        onClick={() => setStep('info')}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        className="flex-1 bg-orange-500 text-white hover:bg-orange-600"
                                        onClick={handleApprove}
                                        disabled={isApprovePending || isApproveConfirming}
                                    >
                                        {isApprovePending ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirm...</>
                                        ) : isApproveConfirming ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Approving...</>
                                        ) : (
                                            'Approve WETH'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                        
                        {/* Dispute Step */}
                        {step === 'dispute' && (
                            <div className="space-y-4">
                                <div className="p-4 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                                    <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="w-4 h-4 text-[#00FF66]" />
                                        <span className="text-[#00FF66] font-semibold">WETH Approved</span>
                                    </div>
                                    <p className="text-sm text-white/60">
                                        Ready to submit your dispute to UMA Oracle
                                    </p>
                                </div>
                                
                                {disputeHash && (
                                    <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                                        <div className="text-xs text-orange-400 mb-1">Dispute Transaction</div>
                                        <a 
                                            href={getExplorerUrl(chainId, 'tx', disputeHash)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-mono text-xs text-white hover:text-orange-400 flex items-center gap-1"
                                        >
                                            {disputeHash.slice(0, 10)}...{disputeHash.slice(-8)}
                                            <ExternalLink className="w-3 h-3" />
                                        </a>
                                    </div>
                                )}
                                
                                {disputeError && (
                                    <Alert className="border-red-500/30 bg-red-500/10">
                                        <XCircle className="w-4 h-4 text-red-400" />
                                        <AlertDescription className="text-red-400 text-sm">
                                            {disputeError.shortMessage || 'Dispute failed. Check your WETH balance.'}
                                        </AlertDescription>
                                    </Alert>
                                )}
                                
                                <Button
                                    className="w-full bg-orange-500 text-white hover:bg-orange-600"
                                    onClick={handleDispute}
                                    disabled={isDisputePending || isDisputeConfirming || isDisputeSuccess}
                                >
                                    {isDisputePending ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirm in Wallet...</>
                                    ) : isDisputeConfirming ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Disputing...</>
                                    ) : isDisputeSuccess ? (
                                        <><CheckCircle className="w-4 h-4 mr-2" />Dispute Submitted!</>
                                    ) : (
                                        <><Gavel className="w-4 h-4 mr-2" />Submit Dispute</>
                                    )}
                                </Button>
                                
                                <p className="text-xs text-white/40 text-center">
                                    UMA token holders will vote on the correct outcome.
                                    The voting process takes 48-72 hours.
                                </p>
                            </div>
                        )}
                    </>
                )}
                
                {/* Link to UMA Oracle */}
                <a 
                    href="https://oracle.uma.xyz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#FFD700] hover:underline"
                >
                    View on UMA Oracle <ExternalLink className="w-3 h-3" />
                </a>
            </CardContent>
        </Card>
    );
}
